import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Calendar, Clock, User, MapPin, DollarSign, CheckCircle, XCircle, AlertCircle, Globe } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { getBookingTimeDisplay, getTimezoneAbbr } from '../utils/timezoneUtils';

const UserBookingsPage = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, confirmed, completed, cancelled

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/bookings/user`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to fetch bookings',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while fetching bookings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300', icon: AlertCircle, text: 'Pending' },
      confirmed: { color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300', icon: CheckCircle, text: 'Confirmed' },
      completed: { color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300', icon: CheckCircle, text: 'Completed' },
      cancelled: { color: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300', icon: XCircle, text: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.text}
      </Badge>
    );
  };

  const filteredBookings = bookings.filter(booking => {
    if (filter === 'all') return true;
    return booking.status === filter;
  });

  const stats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    completed: bookings.filter(b => b.status === 'completed').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-black">
            My Bookings
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">View and manage your trainer session bookings</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div 
            className={`rounded-lg border-2 cursor-pointer hover:shadow-lg transition-all bg-white dark:bg-gray-800 ${
              filter === 'all' 
                ? 'border-red-500 dark:border-red-400 ring-2 ring-red-500/30 dark:ring-red-400/30 shadow-lg' 
                : 'border-red-200 dark:border-red-800'
            }`} 
            onClick={() => setFilter('all')}
          >
            <div className="pt-6 pb-4 px-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.total}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Total Bookings</p>
              </div>
            </div>
          </div>
          <div 
            className={`rounded-lg border-2 cursor-pointer hover:shadow-lg transition-all bg-white dark:bg-gray-800 ${
              filter === 'pending' 
                ? 'border-yellow-500 dark:border-yellow-400 ring-2 ring-yellow-500/30 dark:ring-yellow-400/30 shadow-lg' 
                : 'border-yellow-200 dark:border-yellow-800'
            }`} 
            onClick={() => setFilter('pending')}
          >
            <div className="pt-6 pb-4 px-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.pending}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Pending</p>
              </div>
            </div>
          </div>
          <div 
            className={`rounded-lg border-2 cursor-pointer hover:shadow-lg transition-all bg-white dark:bg-gray-800 ${
              filter === 'confirmed' 
                ? 'border-green-500 dark:border-green-400 ring-2 ring-green-500/30 dark:ring-green-400/30 shadow-lg' 
                : 'border-green-200 dark:border-green-800'
            }`} 
            onClick={() => setFilter('confirmed')}
          >
            <div className="pt-6 pb-4 px-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.confirmed}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Confirmed</p>
              </div>
            </div>
          </div>
          <div 
            className={`rounded-lg border-2 cursor-pointer hover:shadow-lg transition-all bg-white dark:bg-gray-800 ${
              filter === 'completed' 
                ? 'border-blue-500 dark:border-blue-400 ring-2 ring-blue-500/30 dark:ring-blue-400/30 shadow-lg' 
                : 'border-blue-200 dark:border-blue-800'
            }`} 
            onClick={() => setFilter('completed')}
          >
            <div className="pt-6 pb-4 px-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.completed}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Completed</p>
              </div>
            </div>
          </div>
          <div 
            className={`rounded-lg border-2 cursor-pointer hover:shadow-lg transition-all bg-white dark:bg-gray-800 ${
              filter === 'cancelled' 
                ? 'border-red-500 dark:border-red-400 ring-2 ring-red-500/30 dark:ring-red-400/30 shadow-lg' 
                : 'border-red-200 dark:border-red-800'
            }`} 
            onClick={() => setFilter('cancelled')}
          >
            <div className="pt-6 pb-4 px-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.cancelled}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Cancelled</p>
              </div>
            </div>
          </div>
        </div>

        {/* Active Filter Badge */}
        {filter !== 'all' && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">Filtered by:</span>
            <Badge className="capitalize bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
              {filter}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilter('all')}
              className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
            >
              Clear Filter
            </Button>
          </div>
        )}

        {/* Bookings List */}
        {loading ? (
          <div className="border-2 border-red-200 dark:border-red-800 rounded-lg bg-white dark:bg-gray-800">
            <div className="py-12">
              <p className="text-center text-gray-500 dark:text-gray-400">Loading bookings...</p>
            </div>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="border-2 border-red-200 dark:border-red-800 rounded-lg bg-white dark:bg-gray-800">
            <div className="py-12">
              <div className="text-center">
                <Calendar className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  {filter === 'all' ? 'No Bookings Yet' : `No ${filter} bookings`}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  {filter === 'all' 
                    ? 'Start booking sessions with trainers to see them here'
                    : `You don't have any ${filter} bookings`
                  }
                </p>
                {filter !== 'all' && (
                  <Button
                    onClick={() => setFilter('all')}
                    className="bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900"
                  >
                    View All Bookings
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((booking) => (
              <div key={booking.id} className="border-2 border-red-200 dark:border-red-800 hover:shadow-lg transition-shadow rounded-lg bg-white dark:bg-gray-800">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <User className="w-5 h-5 text-red-600 dark:text-red-400" />
                        {booking.trainer_name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {booking.specialization}
                      </p>
                    </div>
                    {getStatusBadge(booking.status)}
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                      <Calendar className="w-4 h-4 text-red-600 dark:text-red-400" />
                      <span className="text-sm">
                        {new Date(booking.date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                      <Clock className="w-4 h-4 text-red-600 dark:text-red-400" />
                      <span className="text-sm">
                        {(() => {
                          const timeDisplay = getBookingTimeDisplay(booking, booking.trainer_timezone, user?.timezone);
                          if (timeDisplay.showBoth) {
                            return (
                              <span className="flex flex-col">
                                <span className="font-medium">{timeDisplay.convertedTime} <span className="text-xs text-gray-500">({getTimezoneAbbr(user?.timezone)} - your time)</span></span>
                                <span className="text-xs text-gray-400">Trainer: {timeDisplay.originalTime} {getTimezoneAbbr(booking.trainer_timezone)}</span>
                              </span>
                            );
                          }
                          return `${booking.time} (${booking.duration} minutes)`;
                        })()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                      <DollarSign className="w-4 h-4 text-red-600 dark:text-red-400" />
                      <span className="text-sm font-semibold">
                        ${booking.price}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span>Booked on {new Date(booking.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default UserBookingsPage;
